import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Button, Typography } from '@canvas-logic/clp-shared-ui';
import { Modal } from '@/ui-kit/components/Modal/Modal';
import { ModalCloseButton } from '@/ui-kit/components/ModalCloseButton/ModalCloseButton';
import { ModalName, modalStore } from '@/ui-kit/components/Modal/ModalStore';
import { Bootstrapper } from '@/ui-kit/domain/Bootstrapper';

import loaderSvg from './loader.svg';
import styles from './HighQualityRenderModal.module.css';

type RenderState = 'loading' | 'complete';

interface IHighQualityRenderModalProps {
  bootstrapper: Bootstrapper;
}

export const HighQualityRenderModal = observer(function HighQualityRenderModal({
  bootstrapper,
}: IHighQualityRenderModalProps) {
  const [renderState, setRenderState] = useState<RenderState>('loading');
  const [imageUrl, setImageUrl] = useState<string | null>(null);

  const isOpen = modalStore.getIsOpened(ModalName.HIGH_QUALITY_RENDER);

  const handleClose = useCallback(() => {
    modalStore.closeModal();
    setRenderState('loading');
    setImageUrl(null);
  }, []);

  const handleDownload = useCallback(() => {
    if (!imageUrl) return;

    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'high-quality-render.png';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [imageUrl]);

  useEffect(() => {
    if (!isOpen) return;

    setRenderState('loading');
    setImageUrl(null);

    // Use cached hqrImageUrl from bootstrapper (already computed for current variant)
    const resolvedImage = bootstrapper.hqrImageUrl;

    if (resolvedImage) {
      // Small delay to show loading state for better UX
      const timer = setTimeout(() => {
        setImageUrl(resolvedImage);
        setRenderState('complete');
      }, 300);

      return () => clearTimeout(timer);
    } else {
      // No image found for current configuration - close modal
      console.warn('No HQR image found for current configuration');
      modalStore.closeModal();
    }
  }, [isOpen, bootstrapper.hqrImageUrl]);

  const isComplete = renderState === 'complete' && !!imageUrl;

  return (
    <Modal
      modalName={ModalName.HIGH_QUALITY_RENDER}
      title="High quality render"
      fullContent={isComplete}
      className={styles.modal}
    >
      {isComplete ? (
        <div className={styles.imageViewContainer}>
          <img src={imageUrl} alt="Rendered image" className={styles.fullImage} />
          <ModalCloseButton onClick={handleClose} className={styles.imageCloseButton} variant="fab" />
          <Button variant="ternary" onClick={handleDownload} className={styles.imageDownloadButton}>
            Download image
          </Button>
        </div>
      ) : (
        <div className={styles.loadingContent}>
          <div className={styles.spinnerContainer}>
            <img src={loaderSvg} alt="Loading" className={styles.spinner} />
          </div>

          <Typography variant="title-md" className={styles.title}>
            Rendering is in process
          </Typography>

          <Typography variant="body-md" color="description" className={styles.subtitle}>
            Please wait...
          </Typography>

          <Button variant="secondary" onClick={handleClose} className={styles.cancelButton}>
            Cancel
          </Button>
        </div>
      )}
    </Modal>
  );
});
